import { BentoCard } from '@/components/bento-card'
import { Button } from '@/components/button'
import { Container } from '@/components/container'
import { Footer } from '@/components/footer'
import { Gradient } from '@/components/gradient'
// import { LogoCloud } from '@/components/logo-cloud' // TODO: Add back with real customer logos
import { Navbar } from '@/components/navbar'
import { AIBookingDemo } from '@/components/ai-booking-demo'
import { DemoModal } from '@/components/demo-modal'
// import { Testimonials } from '@/components/testimonials' // TODO: Add back with real testimonials
import { Heading, Subheading } from '@/components/text'
import { useEffect, useState } from 'react'

function Hero({ onRequestDemo }) {
  return (
    <div className="relative">
      <Gradient className="absolute inset-2 bottom-0 rounded-4xl ring-1 ring-black/5 ring-inset" />
      <Container className="relative">
        <Navbar />
        <div className="pt-16 pb-24 sm:pt-24 sm:pb-32 md:pt-32 md:pb-48">
          <h1 className="font-display text-6xl/[0.9] font-medium tracking-tight text-balance text-gray-950 sm:text-8xl/[0.8] md:text-9xl/[0.8]">
            Elevate your golf course.
          </h1>
          <p className="mt-8 max-w-lg text-xl/7 font-medium text-gray-950/75 sm:text-2xl/8">
            AI-powered solutions that transform member experience, streamline operations, and boost revenue.
          </p>
          <div className="mt-12 flex flex-col gap-x-6 gap-y-4 sm:flex-row">
            <Button href="#modern-solutions">Explore Solutions</Button>
            <Button variant="secondary" onClick={onRequestDemo}>
              Request Demo
            </Button>
          </div>
        </div>
      </Container>
    </div>
  )
}

function FeatureSection() {
  return (
    <div className="overflow-hidden" id="modern-solutions">
      <Container className="pb-24">
      <Subheading>Modern Solutions</Subheading>
        <Heading as="h2" className="max-w-3xl mt-2">
          AI that transforms every golfer interaction into a premium experience.
        </Heading>
        <p className="mt-6 max-w-2xl text-lg text-gray-600">
          From booking tee times to ordering at the turn, our AI agents handle it all through natural conversation - 
          via chat, voice, or phone. Your members get instant, personalized service 24/7.
        </p>
        <div className="mt-12">
          <AIBookingDemo />
        </div>
      </Container>
    </div>
  )
}

function BentoSection() {
  return (
    <Container id="features">
      <Subheading>Feedback & POS Integration</Subheading>
      <Heading as="h3" className="mt-2 max-w-3xl">
        Transform operations with intelligent automation.
      </Heading>

      <div className="mt-10 grid grid-cols-1 gap-4 sm:mt-16 lg:grid-cols-6 lg:grid-rows-2">
        <BentoCard
          eyebrow="Insights"
          title="Real-time member sentiment"
          description="AI analyzes feedback across all touchpoints - from the pro shop to the 19th hole - giving you instant insights into member satisfaction."
          graphic={
            <div className="h-80 flex items-center justify-center p-8">
              <img src="/sentiment-analysis.svg" alt="Sentiment Analysis" className="w-full h-full object-contain" />
            </div>
          }
          fade={['bottom']}
          className="max-lg:rounded-t-4xl lg:col-span-3 lg:rounded-tl-4xl"
        />
        <BentoCard
          eyebrow="Analytics"
          title="Predictive trend analysis"
          description="Identify patterns before they become problems. Know which members are at risk and what drives loyalty at your facility."
          graphic={
            <div className="absolute inset-0 flex items-center justify-center p-8">
              <img src="/trend-analysis.svg" alt="Trend Analysis" className="w-full h-full object-contain" />
            </div>
          }
          fade={['bottom']}
          className="lg:col-span-3 lg:rounded-tr-4xl"
        />
        <BentoCard
          eyebrow="Efficiency"
          title="Unified transaction system"
          description="Integrate tee sheet bookings, pro shop sales, F&B orders, and lesson bookings into one seamless system."
          graphic={
            <div className="flex size-full items-center justify-center p-6">
              <img src="/unified-pos.svg" alt="Unified POS System" className="w-full h-full object-contain" />
            </div>
          }
          className="lg:col-span-2 lg:rounded-bl-4xl"
        />
        <BentoCard
          eyebrow="Revenue"
          title="Smart upselling"
          description="AI-powered recommendations increase average transaction value by suggesting relevant products based on member preferences."
          graphic={
            <div className="flex size-full items-center justify-center p-6">
              <img src="/smart-upselling.svg" alt="Smart Upselling" className="w-full h-full object-contain" />
            </div>
          }
          className="lg:col-span-2"
        />
        <BentoCard
          eyebrow="Reporting"
          title="Comprehensive analytics"
          description="Track revenue streams, identify peak times, and optimize pricing strategies with real-time business intelligence."
          graphic={
            <div className="flex size-full items-center justify-center p-6">
              <img src="/analytics-dashboard.svg" alt="Analytics Dashboard" className="w-full h-full object-contain" />
            </div>
          }
          className="max-lg:rounded-b-4xl lg:col-span-2 lg:rounded-br-4xl"
        />
      </div>
    </Container>
  )
}

function DarkBentoSection() {
  return (
    <div className="mx-2 mt-2 rounded-4xl bg-gray-900 py-32">
      <Container>
        <div className="text-center">
          <Subheading dark>AI Voice Coaching</Subheading>
          <Heading as="h3" dark className="mt-2 max-w-3xl mx-auto">
            Personalized golf coaching powered by ai. <br/><br/>Coming soon.
          </Heading>
          {/* SVG Illustration */}
          <div className="mt-12 flex justify-center">
            <div className="w-full max-w-md rounded-lg">
              <img 
                src="/coming-soon-coaching.svg" 
                alt="AI Golf Coaching Coming Soon" 
                className="w-full h-auto rounded-2xl"
              />
            </div>
          </div>
          <p className="mt-6 max-w-2xl mx-auto text-lg text-gray-300">
            Revolutionary AI-driven instruction that analyzes your swing, provides real-time feedback, and creates 
            personalized improvement plans.
          </p>
          
          
        </div>
      </Container>
    </div>
  )
}

export default function HomePage() {
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    document.title = 'AscendGolf - AI Solutions for the Golf Industry'
  }, [])

  const handleRequestDemo = () => {
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  return (
    <div className="overflow-hidden">
      <Hero onRequestDemo={handleRequestDemo} />
      <main>
        {/* TODO: Add customer logos section once we have real customers
        <Container className="mt-10">
          <p className="text-center text-sm font-medium text-gray-600 mb-6">Trusted by Premier Golf Facilities</p>
          <LogoCloud />
        </Container>
        */}
        <div className="bg-linear-to-b from-white from-50% to-gray-100 py-32">
          <FeatureSection />
          <BentoSection />
        </div>
        <DarkBentoSection />
      </main>
      {/* <Testimonials /> */} {/* TODO: Add back with real customer testimonials */}
      <Footer onRequestDemo={handleRequestDemo} />
      
      {/* Demo Modal */}
      <DemoModal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal} 
      />
    </div>
  )
}